import React from 'react'
import { cn } from '@/lib/utils'
import { Plus } from 'lucide-react'

// MIDNIGHT pastel tints for icon chip
export type EmptyStateTone = 'mint' | 'peach' | 'lavender' | 'butter' | 'blush' | 'sky' | 'neutral'

const TONE_HEX: Record<EmptyStateTone, string | null> = {
  mint:     '#A8E6CE',
  peach:    '#FFC5A0',
  lavender: '#C7B9F0',
  butter:   '#F3DF93',
  blush:    '#F4B5BD',
  sky:      '#B5D3F0',
  neutral:  null,
}

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `${r},${g},${b}`
}

interface EmptyStateProps {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: {
    label: string
    onClick: () => void
  }
  /** NEW — secondary ghost action rendered below primary */
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  /** NEW — affects icon chip color only (default: 'neutral') */
  tone?: EmptyStateTone
  className?: string
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  tone = 'neutral',
  className,
}: EmptyStateProps) {
  // Strip leading "+ " from action.label if present — we render the icon
  const actionLabel = action?.label.replace(/^\+\s*/, '') ?? ''

  const tintHex = TONE_HEX[tone]

  const iconChipStyle: React.CSSProperties = tintHex
    ? {
        width: 56,
        height: 56,
        borderRadius: 9999,
        background: `rgba(${hexToRgb(tintHex)},0.12)`,
        color: tintHex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
        flexShrink: 0,
      }
    : {
        width: 56,
        height: 56,
        borderRadius: 9999,
        background: 'rgba(255,255,255,0.06)',
        color: 'var(--midnight-text3, rgba(255,255,255,0.40))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
        flexShrink: 0,
      }

  return (
    <div
      className={cn('flex flex-col items-center justify-center text-center', className)}
      style={{
        background: 'rgba(0,0,0,0.3)',
        border: '0.5px solid var(--midnight-border, rgba(255,255,255,0.08))',
        borderRadius: 'var(--midnight-r-lg, 24px)',
        padding: 40,
      }}
    >
      {icon && (
        <div style={iconChipStyle}>
          {/* Clone the icon to apply appropriate sizing if it's a Lucide element */}
          {React.isValidElement(icon)
            ? React.cloneElement(icon as React.ReactElement<{ width?: number; height?: number; style?: React.CSSProperties }>, {
                width: 24,
                height: 24,
                style: { flexShrink: 0 },
              })
            : icon}
        </div>
      )}

      <h3
        style={{
          margin: '0 0 8px',
          fontSize: 20,
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: 'var(--midnight-text, #FFFFFF)',
        }}
      >
        {title}
      </h3>

      {description && (
        <p
          style={{
            margin: '0 0 24px',
            fontSize: 15,
            lineHeight: 1.5,
            color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
            maxWidth: 320,
          }}
        >
          {description}
        </p>
      )}

      {action && (
        <button
          type="button"
          onClick={action.onClick}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 44,
            padding: '0 20px',
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 9999,
            background: '#FFFFFF',
            color: '#000000',
            border: 'none',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            marginBottom: secondaryAction ? 8 : 0,
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.88' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
        >
          <Plus style={{ width: 16, height: 16 }} />
          {actionLabel}
        </button>
      )}

      {secondaryAction && (
        <button
          type="button"
          onClick={secondaryAction.onClick}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 40,
            padding: '0 16px',
            fontSize: 14,
            fontWeight: 500,
            borderRadius: 9999,
            background: 'transparent',
            color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
            border: '0.5px solid var(--midnight-border, rgba(255,255,255,0.08))',
            cursor: 'pointer',
            transition: 'color 0.2s ease, border-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLButtonElement
            el.style.color = '#FFFFFF'
            el.style.borderColor = 'var(--midnight-border-bright, rgba(255,255,255,0.14))'
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLButtonElement
            el.style.color = 'var(--midnight-text2, rgba(255,255,255,0.60))'
            el.style.borderColor = 'var(--midnight-border, rgba(255,255,255,0.08))'
          }}
        >
          {secondaryAction.label}
        </button>
      )}
    </div>
  )
}
